import { flowEvidenceFor } from "./buyerFundingFlowSmoke";
import { buildFeasibleSpecPackage, buildRedactedSummary } from "./feasiblePackage";
import { buildBuyerPortalSubmissionPacket } from "./buyerPortal";
import { buildReceiptPackage } from "./receipt";
import type { BuyerFundingReview } from "./types";

function crossReferenceCsv(rows: { field: string; source: string; evidence: string; result: string }[]): string {
  const header = "field,source,evidence,result";
  const lines = rows.map((row) =>
    [row.field, row.source, row.evidence, row.result]
      .map((cell) => `"${String(cell).replaceAll('"', '""')}"`)
      .join(",")
  );
  return [header, ...lines].join("\n");
}

export async function createSaveBackZipBlob(review: BuyerFundingReview): Promise<Blob> {
  const { default: JSZip } = await import("jszip");
  const pkg = await buildReceiptPackage(review);
  const feasible = buildFeasibleSpecPackage(review);
  const buyerPacket = buildBuyerPortalSubmissionPacket(review);
  const redacted = buildRedactedSummary(review);
  const zip = new JSZip();

  zip.file(`receipt/${review.reviewNumber}-receipt.html`, pkg.receiptHtml);
  zip.file(`receipt/${review.reviewNumber}-save-back-note.txt`, pkg.saveBackNote);
  zip.file(`manifest/source-manifest.json`, JSON.stringify(pkg.sourceManifest, null, 2));
  zip.file(`manifest/event-log.json`, JSON.stringify(pkg.eventLog, null, 2));
  zip.file(`manifest/field-cross-reference.csv`, crossReferenceCsv(pkg.fieldCrossReference));
  zip.file(`manifest/hashes.json`, JSON.stringify({ receiptHash: pkg.receiptHash, exportedAt: new Date().toISOString() }, null, 2));
  zip.file(`manifest/source-packet.json`, pkg.packetJson);
  zip.file(`manifest/policy-gates.json`, JSON.stringify(feasible.policyGates, null, 2));
  zip.file(`manifest/buyer-collection-packet.json`, JSON.stringify(feasible.buyerCollectionPacket, null, 2));
  zip.file(`manifest/accounting-reconciliation.json`, JSON.stringify(feasible.accountingReconciliation, null, 2));
  zip.file(`manifest/provider-evidence-schema.json`, JSON.stringify(feasible.providerEvidenceSchema, null, 2));
  zip.file(`manifest/manager-exception-queue.json`, JSON.stringify(feasible.managerExceptionQueue, null, 2));
  zip.file(`manifest/audit-package-manifest.json`, JSON.stringify(feasible.auditPackageManifest, null, 2));
  zip.file(`manifest/integration-status.json`, JSON.stringify(feasible.integrationStatus, null, 2));
  zip.file(`manifest/buyer-portal-submissions.json`, JSON.stringify(buyerPacket, null, 2));
  zip.file(`manifest/redacted-summary.json`, JSON.stringify(redacted, null, 2));
  zip.file(`manifest/feasible-spec-coverage.json`, JSON.stringify(feasible.coverageSummary, null, 2));

  const flow = flowEvidenceFor(review);
  if (flow?.docuSignSignedDocumentBase64) {
    const bytes = Uint8Array.from(atob(flow.docuSignSignedDocumentBase64), (c) => c.charCodeAt(0));
    zip.file(`acknowledgments/${review.fileNumber}-buyer-funding-acknowledgment-signed.html`, bytes);
  }

  return zip.generateAsync({ type: "blob" });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
